import {CANONICAL_VERSION,CONTENT_RECIPES,resolveRecipe} from '../../content/canonical';
// A recipe shared with cooking buddies is an explicit snapshot: the recipe plus the user's own
// ingredients, utensils and units it uses (built-in ones exist in every copy of Chefi). Photos stay
// on the device. Saving a shared recipe makes an attributed copy with a new id and never replaces
// anything already in the saver's library. Deleting the post does not remove copies already saved
// (the app says so before sharing).
import type { Id, Ingredient, Recipe, Utensil } from '../types';
import type { CustomUnit, UnitRef } from './units';

export const SHARE_FORMAT = 'chefi-share';
export type RecipeShare = {
  format: typeof SHARE_FORMAT;
  version: 1;
  recipe: Recipe;
  ingredients: Record<Id, Ingredient>;
  utensils: Record<Id, Utensil>;
  units: Record<Id, CustomUnit>;
};
type Library = { ingredients: Record<Id, Ingredient>; utensils: Record<Id, Utensil>; units: Record<Id, CustomUnit> };

const noImage = <T extends { image?: unknown }>(item: T): T => {
  const { image: _image, ...rest } = item;
  return rest as T;
};
const customUnit = (u: UnitRef) => (u && 'custom' in u ? u.custom : null);

export function buildShare(recipe: Recipe, mine: Library): RecipeShare {
  const lines = [...recipe.ingredients, ...recipe.steps.flatMap(s => s.ingredients)];
  const utensilLines = [...recipe.utensils, ...recipe.steps.flatMap(s => s.utensils)];
  const pick = <T>(all: Record<Id, T>, ids: (Id | null)[], map: (v: T) => T = v => v) =>
    Object.fromEntries([...new Set(ids)].filter((id): id is Id => !!id && !!all[id]).map(id => [id, map(all[id])]));
  const { source: _source, ...own } = recipe;
  return {
    format: SHARE_FORMAT,
    version: 1,
    recipe: { ...noImage(own), steps: recipe.steps.map(noImage) } as Recipe,
    ingredients: pick(mine.ingredients, lines.map(l => l.ingredientId), noImage),
    utensils: pick(mine.utensils, utensilLines.map(l => l.utensilId), noImage),
    units: pick(mine.units, lines.map(l => customUnit(l.unit))),
  };
}

export function buildSharePayload(recipe:Recipe,mine:Library):unknown {
  const base=CONTENT_RECIPES[recipe.id];
  if(base&&(recipe.canonical?.version===CANONICAL_VERSION||!recipe.canonical&&recipe.updatedAt===0)){
    const comparable=(r:Recipe)=>JSON.stringify({...r,canonical:undefined,updatedAt:0});
    if(comparable(base)===comparable(recipe))return {format:SHARE_FORMAT,version:2,reference:{id:base.id,version:CANONICAL_VERSION},recipe:{title:base.title}};
  }
  return buildShare(recipe,mine);
}

/** The share inside a post, if it is one this version understands. */
export function parseShare(value: unknown): RecipeShare | null {
  const v = value as Partial<RecipeShare> | null;
  if(!v||v.format!==SHARE_FORMAT)return null;
  if((v as {version?:number}).version===2){
    const ref=(v as unknown as {reference?:{id:string;version:string}}).reference;
    if(!ref||typeof ref.id!=='string'||typeof ref.version!=='string')return null;
    const recipe=resolveRecipe({...ref,savedAt:0});
    return recipe?{format:SHARE_FORMAT,version:1,recipe,ingredients:{},utensils:{},units:{}}:null;
  }
  if(v.version!==1)return null;
  const r = v.recipe as Partial<Recipe> | undefined;
  if (!r || typeof r.title !== 'string' || !Array.isArray(r.ingredients) || !Array.isArray(r.steps) || !Array.isArray(r.utensils)) return null;
  const record = (x: unknown) => (x && typeof x === 'object' && !Array.isArray(x) ? x : {});
  return { ...(v as RecipeShare), ingredients: record(v.ingredients) as never, utensils: record(v.utensils) as never, units: record(v.units) as never };
}

/** What to add to the saver's library: a new recipe id, and only the items they do not have yet. */
export function adoptShare(share: RecipeShare, mine: Library, newId: string, author: string, now: number) {
  const missing = <T>(theirs: Record<Id, T>, own: Record<Id, T>) => Object.fromEntries(Object.entries(theirs).filter(([id]) => !own[id]));
  const targetId=share.recipe.canonical?share.recipe.id:newId;
  return {
    recipes: { [targetId]: { ...share.recipe, id: targetId, source: { author, sharedAt: now }, updatedAt: now } as Recipe },
    ingredients: missing(share.ingredients, mine.ingredients) as Record<Id, Ingredient>,
    utensils: missing(share.utensils, mine.utensils) as Record<Id, Utensil>,
    units: missing(share.units, mine.units) as Record<Id, CustomUnit>,
  };
}
